import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { User, FileText, Briefcase, DollarSign, CheckCircle, AlertCircle } from 'lucide-react';
import CustomerForm from './CustomerForm';
import DocumentsForm from './DocumentsForm';
import JobAvailabilityForm from './JobAvailabilityForm';
import RegistrationFeeForm from './RegistrationFeeForm';
import axios from 'axios';
import './MultiStepForm.css';

const url = process.env.REACT_APP_BACKEND_URL;

const MultiStepForm = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    customer: null,
    documents: null,
    jobAvailability: null,
    registrationFee: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const steps = [
    { number: 1, name: 'Customer Details', icon: User, completed: !!formData.customer },
    { number: 2, name: 'Documents', icon: FileText, completed: !!formData.documents },
    { number: 3, name: 'Job Availability', icon: Briefcase, completed: !!formData.jobAvailability },
    { number: 4, name: 'Registration Fee', icon: DollarSign, completed: !!formData.registrationFee },
    { number: 5, name: 'Complete', icon: CheckCircle, completed: false },
  ];

  const updateFormData = (stepName, data) => {
    console.log(`📝 Updating ${stepName} data:`, data);
    setFormData(prev => ({
      ...prev,
      [stepName]: data
    }));
  };

  // THESE FUNCTIONS ONLY COLLECT DATA - NO API CALLS
  const handleCustomerSubmit = (data) => {
    console.log('✅ Customer data collected (NO API CALL)');
    updateFormData('customer', data);
    setStep(2);
  };

  const handleDocumentsSubmit = (data) => {
    console.log('✅ Documents data collected (NO API CALL)');
    updateFormData('documents', data);
    setStep(3);
  };

  const handleJobAvailabilitySubmit = (data) => {
    console.log('✅ Job availability data collected (NO API CALL)');
    updateFormData('jobAvailability', data);
    setStep(4);
  };

  const handleRegistrationFeeSubmit = (data) => {
    console.log('✅ Registration fee data collected (NO API CALL)');
    updateFormData('registrationFee', data);
    setStep(5);
  };

  // ONLY THIS FUNCTION MAKES API CALLS - at the end
  const handleFinalSubmit = async () => {
    // Check if all data is collected
    if (!formData.customer || !formData.documents || !formData.jobAvailability || !formData.registrationFee) {
      toast.error('Please complete all steps before submitting');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Step 1: Create Customer
      console.log('🚀 Creating customer...');
      const customerResponse = await axios.post(`${url}/api/customer`, formData.customer);
      if (!customerResponse.data.success) throw new Error('Failed to create customer');
      const customer = customerResponse.data.data;
      console.log('✅ Customer created:', customer._id);

      // Step 2: Create Documents with customer ID
      console.log('🚀 Creating documents...');
      const documentsFormData = new FormData();
      documentsFormData.append('Document', formData.documents.Document);
      documentsFormData.append('issue_date', formData.documents.issue_date);
      documentsFormData.append('expired_date', formData.documents.expired_date);
      documentsFormData.append('customer', customer._id);
      if (formData.documents.photos) documentsFormData.append('photos', formData.documents.photos);
      if (formData.documents.Documentupload) documentsFormData.append('Documentupload', formData.documents.Documentupload);

      const documentsResponse = await axios.post(`${url}/api/documents`, documentsFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (!documentsResponse.data.success) throw new Error('Failed to create documents');
      console.log('✅ Documents created successfully');

      // Step 3: Create Job Availability with customer ID
      console.log('🚀 Creating job availability...');
      const jobResponse = await axios.post(`${url}/api/jobavailabity`, {
        jobname: formData.jobAvailability.jobname_id, // Use the ID
        section: formData.jobAvailability.section,
        customer: customer._id
      });
      if (!jobResponse.data.success) throw new Error('Failed to create job availability');
      console.log('✅ Job availability created successfully');

      // Step 4: Create Registration Fee with customer ID and FIXED amount of $10
      console.log('🚀 Creating registration fee...');
      const feeResponse = await axios.post(`${url}/api/registrationfee`, {
        customer: customer._id,
        amount: 10, // Fixed amount $10
        bankname: formData.registrationFee.bank_id,
        description: `Registration fee for ${customer.fullname} - Bank: ${formData.registrationFee.bank_name}`,
        payment_date: new Date().toISOString().split('T')[0]
      });
      if (!feeResponse.data.success) throw new Error('Failed to create registration fee');
      console.log('✅ Registration fee created successfully');

      toast.success('All data saved successfully!');
      navigate('/registration/customers');
      
    } catch (error) {
      console.error('❌ Error saving data:', error);
      toast.error(error.message || 'Failed to save registration data');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleNewRegistration = () => {
    setStep(1);
    setFormData({
      customer: null,
      documents: null,
      jobAvailability: null,
      registrationFee: null
    });
  };

  const isAllStepsCompleted = () => {
    return formData.customer && formData.documents && formData.jobAvailability && formData.registrationFee;
  };

  // Debug: Log current formData when step changes
  React.useEffect(() => {
    console.log(`📊 Step ${step} - Data status:`, {
      customer: formData.customer ? '✓' : '✗',
      documents: formData.documents ? '✓' : '✗',
      jobAvailability: formData.jobAvailability ? '✓' : '✗',
      registrationFee: formData.registrationFee ? '✓' : '✗'
    });
  }, [step, formData]);

  return (
    <div className="multistep-container">
      {/* Progress Bar */}
      <div className="progress-bar">
        {steps.map((s) => (
          <div
            key={s.number}
            className={`progress-step ${step >= s.number ? 'active' : ''} ${
              step > s.number ? 'completed' : ''
            }`}
          >
            <div className="step-icon">
              <s.icon size={20} />
            </div>
            <span className="step-name">{s.name}</span>
            {s.completed && <span className="step-check">✓</span>}
          </div>
        ))}
      </div>

      {/* Form Content */}
      <div className="form-content">
        {step === 1 && (
          <CustomerForm 
            onSubmit={handleCustomerSubmit}
            initialData={formData.customer}
          />
        )}

        {step === 2 && (
          <DocumentsForm
            onSubmit={handleDocumentsSubmit}
            onBack={() => setStep(1)}
            customerId={formData.customer?._id}
            initialData={formData.documents}
          />
        )}

        {step === 3 && (
          <JobAvailabilityForm
            onSubmit={handleJobAvailabilitySubmit}
            onBack={() => setStep(2)}
            initialData={formData.jobAvailability}
          />
        )}

        {step === 4 && (
          <RegistrationFeeForm
            onSubmit={handleRegistrationFeeSubmit}
            onBack={() => setStep(3)}
            initialData={formData.registrationFee}
          />
        )}

        {step === 5 && (
          <div className="success-message">
            {isAllStepsCompleted() ? (
              <>
                <CheckCircle size={60} className="success-icon" />
                <h2>All Information Collected!</h2>
                <p>Please review the information below before final submission.</p>
                
                <div className="review-section">
                  <h3>📋 Customer Details</h3>
                  <p><strong>Name:</strong> {formData.customer?.fullname}</p>
                  <p><strong>Email:</strong> {formData.customer?.email}</p>
                  <p><strong>Phone:</strong> {formData.customer?.phone}</p>
                  <p><strong>Address:</strong> {formData.customer?.address}</p>
                  
                  <h3>📄 Documents</h3>
                  <p><strong>Type:</strong> {formData.documents?.Document}</p>
                  <p><strong>Issue Date:</strong> {formData.documents?.issue_date}</p>
                  <p><strong>Expiry Date:</strong> {formData.documents?.expired_date}</p>
                  
                  <h3>💼 Job Availability</h3>
                  <p><strong>Job:</strong> {formData.jobAvailability?.jobname || 'N/A'}</p>
                  <p><strong>Section:</strong> {formData.jobAvailability?.section}</p>
                  
                  <h3>💰 Registration Fee</h3>
                  <p><strong>Amount:</strong> $10.00</p>
                  <p><strong>Bank:</strong> {formData.registrationFee?.bank_name}</p>
                  <p><strong>Account Number:</strong> {formData.registrationFee?.account_number}</p>
                  <p><strong>Reference Number:</strong> {formData.registrationFee?.reference_no || 'N/A'}</p>
                </div>
                
                <div className="button-group">
                  <button onClick={handleBack} className="back-btn">
                    Back
                  </button>
                  <button onClick={handleFinalSubmit} className="submit-final-btn" disabled={isSubmitting}>
                    {isSubmitting ? "Saving..." : "Complete Registration"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <AlertCircle size={60} className="warning-icon" />
                <h2>Registration Incomplete!</h2>
                <p>Please complete all steps to proceed.</p>
                <div className="button-group">
                  <button onClick={() => setStep(1)} className="continue-btn">
                    Continue Registration
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiStepForm;